import { useTranslation } from 'react-i18next';
import { Icon } from '@/presentation/components/common/Icon';
import {
  isMenuHouseActive,
  isMoreSectionPath,
  MOBILE_BOTTOM_NAV_HOUSE_IDS,
  visibleDrawerExtraPrimary,
  visibleMenuHouses,
  type MenuHouseDefinition,
} from '@/application/navigation/menuHouses';

const MORE_ICON =
  'M4 6h4v4H4V6zm6 0h4v4h-4V6zm6 0h4v4h-4V6zM4 12h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4z';

interface MobileBottomNavProps {
  pathname: string;
  hasPermission: (key: string) => boolean;
  isOwner?: boolean;
  onNavigate: (to: string) => void;
  onOpenMore: () => void;
}

/** Bottom nav mobile (&lt; md): Resumen / Clínicas / Finanzas / Más. */
export function MobileBottomNav({
  pathname,
  hasPermission,
  isOwner = false,
  onNavigate,
  onOpenMore,
}: MobileBottomNavProps) {
  const { t } = useTranslation();
  const primary = visibleMenuHouses(hasPermission, 'primary', isOwner);
  const tabs = MOBILE_BOTTOM_NAV_HOUSE_IDS.map((id) => primary.find((h) => h.id === id)).filter(
    (h): h is MenuHouseDefinition => Boolean(h)
  );
  const showMore =
    visibleMenuHouses(hasPermission, 'more', isOwner).length > 0 ||
    visibleDrawerExtraPrimary(hasPermission, isOwner).length > 0;

  if (tabs.length === 0 && !showMore) {
    return null;
  }

  const moreActive = isMoreSectionPath(pathname);

  return (
    <nav
      aria-label={t('menu.bottomNav')}
      className="panel-mobile-bottom-nav fixed inset-x-0 bottom-0 z-30 flex pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="mx-auto flex h-14 w-full max-w-lg items-stretch justify-around px-1">
        {tabs.map((item) => {
          const active = isMenuHouseActive(pathname, item);
          const label = t(item.labelKey);
          return (
            <button
              key={item.id}
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => onNavigate(item.to)}
              className={`panel-mobile-nav-tab ${active ? 'panel-mobile-nav-tab--active' : ''}`}
            >
              <Icon path={item.iconPath} className="h-5 w-5 shrink-0" />
              <span className="max-w-full truncate px-0.5">{label}</span>
            </button>
          );
        })}
        {showMore ? (
          <button
            type="button"
            aria-current={moreActive ? 'page' : undefined}
            onClick={onOpenMore}
            className={`panel-mobile-nav-tab ${moreActive ? 'panel-mobile-nav-tab--active' : ''}`}
          >
            <Icon path={MORE_ICON} className="h-5 w-5 shrink-0" />
            <span className="max-w-full truncate px-0.5">{t('menu.sections.more')}</span>
          </button>
        ) : null}
      </div>
    </nav>
  );
}
